/**
 * Maps any Auth0 error (bad credentials, SSO failures, token exchange
 * failures, blocked accounts, rate limiting, etc.) to a single, generic
 * user-facing explanation — deliberately not a different story per error
 * code. One consistent message is easier to support and doesn't leak which
 * failure mode occurred, so every case reuses the same "unauthorized" copy.
 *
 * Token exchange failures (corrupted PKCE code/state, interrupted callback)
 * are still detected separately via `isTokenExchangeFailure`, but only to
 * decide which action to offer (Reset vs. Try Again) — retrying
 * `loginWithRedirect()` automatically on this class of error is what causes
 * the infinite redirect loop. It does not change the explanation shown.
 */

export interface AuthErrorInfo {
  /** Short modal title. Always the same value — see module docstring. */
  title: string;
  /** User-facing explanation of why sign-in failed. Always the same value — see module docstring. */
  reason: string;
  /** Optional raw Auth0 detail shown in smaller print, for support/debugging. */
  technicalDetail?: string;
  /** True when the failure happened while exchanging the auth code for tokens. */
  isTokenExchangeFailure: boolean;
}

interface Auth0LikeError extends Error {
  error?: string;
  error_description?: string;
}

// Auth0 SDK throws plain Errors (not OAuth error objects) when the redirect
// callback itself is broken — e.g. a stale/replayed code, mismatched state,
// or an interrupted network call during the code/token exchange.
const TOKEN_EXCHANGE_FAILURE_PATTERN =
  /exchange|invalid state|state mismatch|missing.*state|nonce|pkce|code verifier|authorization code/i;

// Single explanation used for every auth failure, regardless of the
// underlying Auth0 error code or failure mode.
const UNAUTHORIZED_TITLE = 'Access Denied';
const UNAUTHORIZED_REASON =
  "Your account isn't authorized to access this dashboard. Contact your admin if you believe this is a mistake.";

export const getAuthErrorInfo = (error?: Error | null): AuthErrorInfo | null => {
  if (!error) {
    return null;
  }

  const auth0Error = error as Auth0LikeError;
  const description = auth0Error.error_description || error.message || '';

  const isTokenExchangeFailure =
    TOKEN_EXCHANGE_FAILURE_PATTERN.test(error.message || '') ||
    TOKEN_EXCHANGE_FAILURE_PATTERN.test(description);

  return {
    title: UNAUTHORIZED_TITLE,
    reason: UNAUTHORIZED_REASON,
    // Raw detail is still exposed as small print for support/debugging —
    // that's diagnostic info, not a second "explanation".
    technicalDetail: description || undefined,
    isTokenExchangeFailure,
  };
};
